import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import BusStopList from './components/BusStopList';
import BusStopSearch from './components/BusStopSearch';
import LanguageSwitcher from './components/LanguageSwitcher';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import RegistrationSuccess from './components/RegistrationSuccess';
import { useAuth } from './context/AuthContext';
import { useFavoritesContext } from './context/FavoritesContext';
import { ErrorMessage, Loading } from './components/common';
import VersionInfo from './components/VersionInfo';
import busStopsService from './services/busStopsService';
import { trackEvent, trackPageView } from './utils/analytics';

// Update document title and meta description based on language
function useDocumentMeta() {
  const { t, i18n } = useTranslation();

  const updateMeta = useCallback(() => {
    document.title = t('meta.title');
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', t('meta.description'));
    }
    document.documentElement.lang = i18n.language;
  }, [t, i18n.language]);

  useEffect(() => {
    updateMeta();
  }, [updateMeta]);

  useEffect(() => {
    i18n.on('languageChanged', updateMeta);
    return () => {
      i18n.off('languageChanged', updateMeta);
    };
  }, [i18n, updateMeta]);
}

function PublicRoute({ children }) {
  const { loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  return children;
}

function RouteAnalytics() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
}

function Dashboard() {
  const { t, i18n } = useTranslation();
  const [filteredBusStops, setFilteredBusStops] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [defaultStops, setDefaultStops] = useState([]);
  const [clearSearchTrigger, setClearSearchTrigger] = useState(0);
  const [error] = useState(null);
  const { user, isAuthenticated, logout } = useAuth();
  const { favorites } = useFavoritesContext();

  // Load default stops from DB when there are no favorites
  useEffect(() => {
    const loadDefaultStops = async () => {
      if (favorites.length === 0) {
        try {
          const stops = await busStopsService.getFirst(3);
          setDefaultStops(stops);
        } catch (err) {
          console.error('Failed to load default stops:', err);
        }
      }
    };
    loadDefaultStops();
  }, [favorites.length]);

  const handleSearchResults = (results, query) => {
    setFilteredBusStops(results);
    setSearchQuery(query || '');
  };

  const handleClearSearch = () => {
    setFilteredBusStops([]);
    setSearchQuery('');
    setClearSearchTrigger(prev => prev + 1);
  };

  const handleLogout = async () => {
    trackEvent('logout_clicked');
    await logout();
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <h1>{t('header.title')}</h1>
          <div className="header-controls">
            <div className="user-info">
              <LanguageSwitcher />
              {isAuthenticated ? (
                <>
                  <span>{t('header.greeting', { email: user.email })}</span>
                  {!user.isEmailVerified && (
                    <span className="email-not-verified"> {t('header.emailNotVerified')}</span>
                  )}
                  <button className="btn btn-logout" onClick={handleLogout}>
                    {t('header.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-login">{t('header.login')}</Link>
                  <Link to="/register" className="btn btn-register">{t('header.register')}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="App-main">
        <ErrorMessage message={error} />

        <BusStopSearch
          onSearchResults={handleSearchResults}
          onClear={handleClearSearch}
          language={i18n.language}
          externalClear={clearSearchTrigger}
        />

        <BusStopList
          busStops={filteredBusStops}
          language={i18n.language}
          searchQuery={searchQuery}
          defaultStops={defaultStops}
          onClearSearch={handleClearSearch}
        />
      </main>

      <footer className="App-footer">
        <VersionInfo />
      </footer>
    </div>
  );
}

function App() {
  useDocumentMeta();

  return (
    <>
      <RouteAnalytics />
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        <Route path="/" element={<Dashboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
